import { DataLoadStatus } from 'data/enums/dataLoadStatus';
import React, { ReactNode } from 'react';
import { isPagedDataObj } from 'features/shared/helpers/objects';
import Alert from 'features/shared/alerts/components/Alert';
import { useSelector } from 'react-redux';
import { DataGridId } from '../dataGrid/DataGridId';
import IAppState from 'infrastructure/store/IAppState';
import { useTranslation } from 'react-i18next';

interface Props {
  children: ReactNode;
  data: any;
  loadStatus: DataLoadStatus | DataLoadStatus[];
  noContentInfo?: string;
  noContentRender?: () => JSX.Element;
  noContentAsAlert?: boolean;
  gridId?: DataGridId;
  isEmpty?: () => boolean;
  isActiveFilter?: () => boolean;
}

const NoContentWrapper = ({ children, data, loadStatus, noContentInfo, noContentRender, noContentAsAlert, gridId, isEmpty, isActiveFilter }: Props) => {
  // translations
  const tLabels = useTranslation("shared\\components").t;
  
  // store
  const gridFilterParams = useSelector<IAppState, string>(
    (state) => gridId ? (state.dataGrid.gridSettings[gridId]?.filtrUrlSearchParams ?? "") : ""
  );

  // helpers
  const isEmptyData = (inputData: any): boolean => {
    // custom isEmpty f-ce
    if (isEmpty) {
      return isEmpty();
    }
    // array type
    else if (Array.isArray(inputData) && (inputData as []).length == 0) {
      return true;
    }
    // paged data result
    else if (isPagedDataObj(inputData) && inputData.count == 0) {
      return true;
    }
    // other types (based on https://262.ecma-international.org/5.1/#sec-9.2)
		else if (!inputData) {
      return true;
    }

    return false;
  };

  const isAppliedFilter = () => {
    if (isActiveFilter) {
      return isActiveFilter();
    } else if (gridFilterParams) {
      return true;
    }

    return false;
  }

  const isAfterFirstLoad = (): boolean => {
    if (Array.isArray(loadStatus)) {
      return loadStatus.every(x => x === "AfterFirstLoad");
    } else {
      return loadStatus === "AfterFirstLoad";
    }
  }

  const getNoContentMessage = () => noContentInfo ?? tLabels("noContentWrapper.noContent");

  // sub-renders
  const renderNoContent = (): JSX.Element => {
    if (noContentRender) {
      return noContentRender();
    } else if (noContentAsAlert) {
      return (
        <Alert alertObj={{ alertType: "Info", message: getNoContentMessage()}} />
      );
    } else {
      return <>{getNoContentMessage()}</>;
    }
  }

  // render
  if (isAfterFirstLoad() && (!isEmptyData(data) || isAppliedFilter())) {
    return (
      <>
        {children}
      </>
    )
  } else if (isAfterFirstLoad()) {
    return (
      <>
        {renderNoContent()}
      </>
    );
  } else {
    return <></>;
  }
}

export default NoContentWrapper;
